/**
 * Server-side utilities for JWT sponsorship.
 *
 * The recommended entry point is `createSponsorshipSigner`, a
 * framework-agnostic factory that reads credentials from environment
 * variables and exposes the two operations each sponsorship endpoint
 * needs: `accessToken()` and `extensionToken(intentOp)`.
 *
 * For custom claims or non-standard flows, `createJwtSigner` is exposed as
 * an escape hatch. The implementation lives here instead of re-exporting
 * `@rhinestone/sdk/jwt-server` directly so downstream app builds do not need
 * to resolve the SDK's optional Express adapter surface.
 *
 * @example Next.js App Router
 * ```ts
 * // app/api/sponsorship/access-token/route.ts
 * import { createSponsorshipSigner } from "@rhinestone/1auth/server";
 *
 * const signer = createSponsorshipSigner();
 *
 * export async function GET() {
 *   // Guard with your own session/auth before minting.
 *   return Response.json({ token: await signer.accessToken() });
 * }
 * ```
 */

import { SignJWT, importJWK } from "jose";
import { isAddress, type Address, type Hex } from "viem";

export { hashMessage, verifyMessageHash } from "./verify";
export { encodeWebAuthnSignature } from "./walletClient/utils";
export type { WebAuthnSignature } from "./types";
// Re-exported so sponsorship endpoints can build a `shouldSponsor` chain
// policy without importing the browser-oriented main entry. `isTestnet` fails
// closed (unknown/unrecognised chain id → false), which is the safe default
// for a sponsorship gate: deny anything we can't positively classify.
export { isTestnet } from "./registry";

export interface JwtCredentials {
  privateKey: JsonWebKey;
  integratorId: string;
  projectId: string;
  appId: string;
  keyId: string;
  audience?: string;
}

export interface SponsorshipFilter {
  chain?: (chain: { id: number }) => boolean | Promise<boolean>;
  account?: (address: Address) => boolean | Promise<boolean>;
  calls?: (
    calls: {
      to: Address;
      value: bigint;
      data: Hex;
    }[],
  ) => boolean | Promise<boolean>;
}

export interface JwtSignerConfig {
  jwt: JwtCredentials;
  shouldSponsor?: SponsorshipFilter;
}

export class SponsorshipDeniedError extends Error {
  constructor() {
    super("Sponsorship denied");
    this.name = "SponsorshipDeniedError";
  }
}

/**
 * Sponsorship signer used by the two endpoints the SDK client calls:
 *
 * - `accessToken()` — mints the app's identity token; return as
 *   `{ token }` from `GET /sponsorship/access-token`.
 * - `extensionToken(intentOp)` — mints a per-intent sponsorship grant;
 *   return as `{ token }` from `POST /sponsorship/extension-token`.
 *   Accepts either the JSON-stringified `intentOp` the SDK sends or a
 *   pre-parsed object.
 */
export interface SponsorshipSigner {
  accessToken(): Promise<string>;
  extensionToken(intentOp: string | object): Promise<string>;
}

export interface CreateSponsorshipSignerOptions {
  /** Override all credentials explicitly (skips env lookup). */
  credentials?: JwtCredentials;
  /** Override the env source (defaults to `process.env`). */
  env?: NodeJS.ProcessEnv;
  /** Optional per-intent sponsorship filter (see `@rhinestone/sdk/jwt-server`). */
  shouldSponsor?: SponsorshipFilter;
}

export interface VerifyOneAuthAccountOptions {
  /** 1auth provider origin, for example `https://passkey.1auth.app`. */
  providerUrl: string;
  /** Smart account address returned by a successful 1auth auth result. */
  address: Address;
  /** Optional app client id forwarded to provider APIs that scope by app. */
  clientId?: string;
  /** Injectable fetch implementation for tests and non-standard runtimes. */
  fetch?: typeof fetch;
}

export interface VerifiedOneAuthAccount {
  address: Address;
  deployed?: boolean;
  chainId?: number;
}

/** Error raised when the provider cannot bind an address to a 1auth account. */
export class OneAuthAccountVerificationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OneAuthAccountVerificationError";
  }
}

const ENV_KEYS = {
  privateKey: "RHINESTONE_JWT_PRIVATE_KEY",
  integratorId: "RHINESTONE_INTEGRATOR_ID",
  projectId: "RHINESTONE_PROJECT_ID",
  appId: "RHINESTONE_APP_ID",
  keyId: "RHINESTONE_KEY_ID",
} as const;

/** Build the documented account-lookup URL for a 1auth provider. */
function accountLookupUrl(providerUrl: string, address: Address): string {
  return new URL(`/api/account/${address.toLowerCase()}`, providerUrl).toString();
}

/** Safely parse a provider account lookup response. */
async function parseAccountLookupResponse(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Verify that an address belongs to a 1auth account known by the configured
 * provider. This is intended for server-side app sessions that already have a
 * fresh challenge signature and need to reject arbitrary ERC-1271 contracts.
 */
export async function verifyOneAuthAccount(
  options: VerifyOneAuthAccountOptions,
): Promise<VerifiedOneAuthAccount> {
  if (!isAddress(options.address)) {
    throw new OneAuthAccountVerificationError(
      "A valid 1auth account address is required",
    );
  }

  const fetchImpl = options.fetch ?? globalThis.fetch;
  if (typeof fetchImpl !== "function") {
    throw new OneAuthAccountVerificationError(
      "No fetch implementation is available to verify the 1auth account",
    );
  }

  const url = accountLookupUrl(options.providerUrl, options.address);
  const headers: HeadersInit = { accept: "application/json" };
  if (options.clientId) headers["x-client-id"] = options.clientId;

  let response: Response;
  try {
    response = await fetchImpl(url, {
      headers,
      cache: "no-store",
    });
  } catch (error) {
    throw new OneAuthAccountVerificationError(
      `Unable to reach the configured 1auth provider: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }

  const body = await parseAccountLookupResponse(response);
  if (!response.ok) {
    const message =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : response.statusText || "account lookup failed";
    throw new OneAuthAccountVerificationError(
      `Configured 1auth provider did not recognize this account (${response.status}: ${message})`,
    );
  }

  const account = body as Partial<VerifiedOneAuthAccount> | null;
  if (!account?.address || !isAddress(account.address)) {
    throw new OneAuthAccountVerificationError(
      "Configured 1auth provider returned a malformed account record",
    );
  }
  if (account.address.toLowerCase() !== options.address.toLowerCase()) {
    throw new OneAuthAccountVerificationError(
      "Configured 1auth provider returned a different account address",
    );
  }

  return {
    address: account.address,
    deployed:
      typeof account.deployed === "boolean" ? account.deployed : undefined,
    chainId: typeof account.chainId === "number" ? account.chainId : undefined,
  };
}

/**
 * RFC 8785 JSON Canonicalization Scheme (JCS).
 *
 * The digest embedded in extension tokens must be byte-for-byte stable across
 * environments, so we canonicalize JSON before hashing instead of trusting
 * ordinary `JSON.stringify` object key order.
 */
function jcsCanonicalise(value: unknown): string {
  return serialize(value);
}

function serialize(value: unknown): string {
  if (value === null || value === undefined) {
    return "null";
  }

  switch (typeof value) {
    case "boolean":
      return value ? "true" : "false";
    case "number":
      if (!Number.isFinite(value)) {
        throw new Error(`JCS: non-finite number: ${value}`);
      }
      return Object.is(value, -0) ? "0" : String(value);
    case "string":
      return JSON.stringify(value);
    case "bigint":
      if (
        value > BigInt(Number.MAX_SAFE_INTEGER) ||
        value < BigInt(-Number.MAX_SAFE_INTEGER)
      ) {
        throw new Error(
          `JCS: BigInt ${value} exceeds safe integer range — convert to string before calling`,
        );
      }
      return String(value);
    default:
      break;
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => serialize(item)).join(",")}]`;
  }

  const obj = value as Record<string, unknown>;
  const members: string[] = [];
  for (const key of Object.keys(obj).sort()) {
    const item = obj[key];
    if (item === undefined) continue;
    members.push(`${JSON.stringify(key)}:${serialize(item)}`);
  }
  return `{${members.join(",")}}`;
}

/**
 * Compute the canonical digest embedded in per-intent sponsorship grants.
 */
async function computeIntentInputDigest(intentInput: unknown): Promise<string> {
  const canonical = jcsCanonicalise(intentInput);
  const encoded = new TextEncoder().encode(canonical);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function parseIntentInput(intentInput: unknown) {
  if (typeof intentInput !== "object" || intentInput === null) {
    throw new Error("intentInput must be a non-null object");
  }

  const input = intentInput as {
    destinationChainId?: number;
    account?: { address?: string };
    destinationExecutions?: { to: Address; value: string; data: Hex }[];
  };

  if (typeof input.destinationChainId !== "number") {
    throw new Error("intentInput.destinationChainId must be a number");
  }

  if (typeof input.account !== "object" || input.account === null) {
    throw new Error("intentInput.account must be a non-null object");
  }

  if (typeof input.account.address !== "string") {
    throw new Error("intentInput.account.address must be a string");
  }

  if (!Array.isArray(input.destinationExecutions)) {
    throw new Error("intentInput.destinationExecutions must be an array");
  }

  return {
    chain: { id: input.destinationChainId },
    account: input.account.address as Address,
    calls: input.destinationExecutions.map((execution) => ({
      to: execution.to,
      value: BigInt(execution.value),
      data: execution.data,
    })),
  };
}

async function shouldSponsor(
  intentInput: unknown,
  filters: SponsorshipFilter,
): Promise<boolean> {
  const parsed = parseIntentInput(intentInput);
  if (filters.chain && !(await filters.chain(parsed.chain))) {
    return false;
  }
  if (filters.account && !(await filters.account(parsed.account))) {
    return false;
  }
  if (filters.calls && !(await filters.calls(parsed.calls))) {
    return false;
  }
  return true;
}

function pickAlg(jwk: JsonWebKey): "ES256" | "ES384" | "ES512" | "RS256" {
  if (jwk.kty === "EC") {
    if (jwk.crv === "P-256") return "ES256";
    if (jwk.crv === "P-384") return "ES384";
    if (jwk.crv === "P-521") return "ES512";
    throw new Error(`Unsupported EC curve: ${jwk.crv}`);
  }
  if (jwk.kty === "RSA") return "RS256";
  throw new Error(`Unsupported JWK kty: ${jwk.kty}`);
}

/**
 * Create a low-level JWT signer compatible with Rhinestone sponsorship flows.
 *
 * This mirrors the upstream SDK behavior while keeping our public server entry
 * point self-contained for app bundlers.
 */
export function createJwtSigner(config: JwtSignerConfig) {
  const {
    jwt: {
      privateKey,
      integratorId,
      projectId,
      appId,
      keyId,
      audience = "rhinestone-api",
    },
    shouldSponsor: filters,
  } = config;

  const alg = pickAlg(privateKey);
  let cachedKey: Awaited<ReturnType<typeof importJWK>> | null = null;

  async function getKey() {
    if (!cachedKey) {
      cachedKey = await importJWK(privateKey, alg);
    }
    return cachedKey;
  }

  return {
    accessToken: async () => {
      const key = await getKey();
      return new SignJWT({ typ: "access", app_id: appId })
        .setProtectedHeader({ alg, kid: keyId })
        .setIssuer(integratorId)
        .setSubject(projectId)
        .setAudience(audience)
        .setIssuedAt()
        .setExpirationTime("1h")
        .sign(key);
    },
    getIntentExtensionToken: async (intentInput: unknown) => {
      if (filters && !(await shouldSponsor(intentInput, filters))) {
        throw new SponsorshipDeniedError();
      }

      const key = await getKey();
      const digest = await computeIntentInputDigest(intentInput);
      return new SignJWT({
        typ: "intent_extension",
        app_id: appId,
        jti: crypto.randomUUID(),
        policy: {
          sponsorship: {
            scope: "intent",
            intent_input: { digest },
          },
        },
      })
        .setProtectedHeader({ alg, kid: keyId })
        .setIssuer(integratorId)
        .setSubject(projectId)
        .setAudience(audience)
        .setIssuedAt()
        .setExpirationTime("5m")
        .sign(key);
    },
  };
}

function readCredentialsFromEnv(env: NodeJS.ProcessEnv): JwtCredentials {
  const missing: string[] = [];
  const read = (key: string) => {
    const value = env[key];
    if (!value) missing.push(key);
    return value ?? "";
  };

  const privateKeyRaw = read(ENV_KEYS.privateKey);
  const integratorId = read(ENV_KEYS.integratorId);
  const projectId = read(ENV_KEYS.projectId);
  const appId = read(ENV_KEYS.appId);
  const keyId = read(ENV_KEYS.keyId);

  if (missing.length > 0) {
    throw new Error(
      `createSponsorshipSigner: missing env var(s): ${missing.join(", ")}`,
    );
  }

  let privateKey: JsonWebKey;
  try {
    privateKey = JSON.parse(privateKeyRaw) as JsonWebKey;
  } catch {
    throw new Error(
      `createSponsorshipSigner: ${ENV_KEYS.privateKey} must be a JSON-encoded JWK`,
    );
  }

  return { privateKey, integratorId, projectId, appId, keyId };
}

export function createSponsorshipSigner(
  options: CreateSponsorshipSignerOptions = {},
): SponsorshipSigner {
  const credentials =
    options.credentials ?? readCredentialsFromEnv(options.env ?? process.env);

  const inner = createJwtSigner({
    jwt: credentials,
    shouldSponsor: options.shouldSponsor,
  });

  return {
    accessToken: () => inner.accessToken(),
    extensionToken: (intentOp) => {
      const intentInput =
        typeof intentOp === "string" ? JSON.parse(intentOp) : intentOp;
      return inner.getIntentExtensionToken(intentInput);
    },
  };
}
